// @ts-nocheck
/**
 * Push Notification Service
 * Handles push notification subscriptions and interactions
 */

import {ConfEnv} from "../config/config";
import privateRequest from "../Infra/http/private.request";
import publicRequest from "../Infra/http/public.request";

// Define API endpoints for push notifications
const API_PUSH = {
  VAPID_PUBLIC_KEY: `${ConfEnv.BASE_URL}/push/vapid-public-key`, // Updated to match the actual endpoint
  SUBSCRIPTIONS: `${ConfEnv.BASE_URL}/push-subscriptions`, // Updated to match the actual endpoint
};

// Variable to store the VAPID public key
let VAPID_PUBLIC_KEY: string | null = null;

/**
 * Fetches the VAPID public key from the server
 * @returns {Promise<string>} The VAPID public key
 */
export async function getVapidPublicKey() {
  if (VAPID_PUBLIC_KEY) {
    return VAPID_PUBLIC_KEY;
  }
  const {data} = await publicRequest.get(API_PUSH.VAPID_PUBLIC_KEY);

  // Check what format the key is returned in
  if (!data?.publicKey) {
    // Changed from vapidPublicKey to publicKey
    throw new Error("Invalid VAPID key response");
  }

  VAPID_PUBLIC_KEY = data.publicKey; // Changed from vapidPublicKey to publicKey
  return VAPID_PUBLIC_KEY;
}

/**
 * Converts a URL-safe base64 string to a Uint8Array for the push subscription
 */
function urlBase64ToUint8Array(base64String: string) {
  if (!base64String) {
    throw new Error("VAPID key is empty or undefined");
  }
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Checks if push notifications are supported by the browser
 */
export function isPushNotificationSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
}

/**
 * Requests permission for push notifications
 * @returns {Promise<string>} The permission status
 */
export async function askUserPermission() {
  if (typeof Notification === "undefined") {
    return "denied";
  }

  try {
    return await Notification.requestPermission();
  } catch (_error) {
    // Safari on iOS might throw here due to browser limitations
    return "denied";
  }
}

/**
 * Creates a push notification subscription
 * @returns {Promise<PushSubscription>} The push subscription object
 */
export async function createNotificationSubscription() {
  // Wait for service worker registration
  const registration = await navigator.serviceWorker.ready;

  // Get the VAPID public key
  const vapidPublicKey = await getVapidPublicKey();

  if (!vapidPublicKey) {
    throw new Error("Failed to get VAPID public key");
  }

  // Subscribe to push notifications
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });

  return subscription;
}

/**
 * Gets the active push subscription
 * @returns {Promise<PushSubscription|null>} The active subscription or null
 */
export async function getUserSubscription() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (_error) {
    return null;
  }
}

/**
 * Sends the push subscription to your server
 * @param {PushSubscription} subscription The user's push subscription
 * @param {string} userId The user's ID for association
 * @returns {Promise<Response>} Server response
 */
export async function sendSubscriptionToServer(subscription: PushSubscription) {
  // Using privateRequest instead of fetch with the actual endpoint
  const {data} = await privateRequest.post(API_PUSH.SUBSCRIPTIONS, {
    subscription,
  });

  return data;
}

/**
 * Unsubscribes from push notifications
 * @returns {Promise<boolean>} Success status
 */
export async function unsubscribeFromPushNotifications() {
  try {
    const subscription = await getUserSubscription();

    if (subscription) {
      await subscription.unsubscribe();

      // Notify your server about the unsubscription using privateRequest
      try {
        await privateRequest.delete(API_PUSH.SUBSCRIPTIONS, {subscription});
      } catch (_serverError) {
        // Continue with the function even if server notification fails
      }

      return true;
    }
    return false;
  } catch (_error) {
    return false;
  }
}
